import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { useGetStripePromise } from '#pplx/frontend/billing/stripe-browser/useStripePromise.js';
import { useIsMounted } from '#pplx/frontend/common/hooks/useIsMounted.js';
import { useCallback } from 'react';
function paymentError(errorCode = 'payment_failed') {
    return new ApiClientsError('API_CLIENTS_ERROR', {
        status: errorCode === 'seat_capacity_stale' ? 409 : 402,
        cause: { detail: { error_code: errorCode } },
    });
}
export function useSeatPaymentAuthentication() {
    const getStripe = useGetStripePromise();
    const isMounted = useIsMounted();
    return useCallback(async (preview, submit) => {
        if (!preview)
            return submit(preview);
        // TODO(kalvin): Remove this temporary opt-in after the backend no longer
        // depends on it and all backend instances have completed that deploy.
        const paymentPreview = { ...preview, supports_payment_action: true };
        let invoiceId;
        let clientSecret;
        try {
            return await submit(paymentPreview);
        }
        catch (error) {
            if (!(error instanceof ApiClientsError) ||
                error.detail?.error_code !== 'payment_action_required') {
                throw error;
            }
            const detail = error.detail;
            if (error.status !== 402 ||
                typeof detail.payment_invoice_id !== 'string' ||
                !detail.payment_invoice_id ||
                typeof detail.payment_intent_client_secret !== 'string' ||
                !detail.payment_intent_client_secret) {
                throw paymentError();
            }
            invoiceId = detail.payment_invoice_id;
            clientSecret = detail.payment_intent_client_secret;
        }
        let authenticationError;
        try {
            if (!isMounted())
                throw paymentError();
            const stripe = await getStripe();
            if (!stripe || !isMounted())
                throw paymentError();
            ({ error: authenticationError } = await stripe.confirmCardPayment(clientSecret));
        }
        catch {
            // Stripe errors can contain the client secret. Only expose our error code.
            throw paymentError();
        }
        if (authenticationError) {
            throw paymentError(authenticationError.code === 'payment_intent_unexpected_state'
                ? 'seat_capacity_stale'
                : 'payment_failed');
        }
        if (!isMounted())
            throw paymentError();
        try {
            return await submit({ ...paymentPreview, payment_invoice_id: invoiceId });
        }
        catch (error) {
            // A resume must not start another authentication cycle or leak its secret.
            if (error instanceof ApiClientsError &&
                error.detail?.error_code === 'payment_action_required') {
                throw paymentError();
            }
            throw error;
        }
    }, [getStripe, isMounted]);
}
//# sourceMappingURL=useSeatPaymentAuthentication.js.map